import { Alley } from '../../../../services/entities/impl/blfApi/schemas/alley';
import { Upload } from '../../../../services/entities/impl/blfApi/schemas/upload';
import { extractAlleyFromFilename } from '../../utils/extractAlleyFromFilename';

/**
 * Options for sorting by alley number
 */
export interface SortByAlleyOptions {
  /** Whether to prioritize files with specific patterns */
  prioritizePatterns?: string[];
  /** Whether to sort in ascending (true) or descending (false) order */
  ascending?: boolean;
  /** Custom property to use for getting alley value (for alleys objects) */
  alleyProperty?: string;
}

/**
 * Default options for sorting
 */
const defaultOptions: SortByAlleyOptions = {
  prioritizePatterns: [],
  ascending: true,
};

/**
 * Sort alleys by their alley identifier
 */
export const sortAlleys = (
  alleys: Alley[] | undefined,
  options?: SortByAlleyOptions
): Alley[] => {
  if (!alleys || alleys.length === 0) return [];

  const opts = { ...defaultOptions, ...options };

  return [...alleys].sort((a, b) => {
    const aAlley = a.alley || '';
    const bAlley = b.alley || '';

    // Use localeCompare with numeric sorting for natural alphanumeric ordering
    const comparison = aAlley.localeCompare(bAlley, undefined, {
      numeric: true,
      sensitivity: 'base',
    });

    return opts.ascending ? comparison : -comparison;
  });
};

/**
 * Sort uploads by the alley number in their filename
 */
export const sortUploads = (
  uploads: Upload[] | undefined,
  options?: SortByAlleyOptions
): Upload[] => {
  if (!uploads || uploads.length === 0) return [];

  const opts = { ...defaultOptions, ...options };

  return [...uploads].sort((a, b) => {
    // Handle priority patterns first (e.g., collection.json)
    for (const pattern of opts.prioritizePatterns || []) {
      const aHasPattern = a.fileName?.includes(pattern);
      const bHasPattern = b.fileName?.includes(pattern);

      if (aHasPattern && !bHasPattern) return -1;
      if (!aHasPattern && bHasPattern) return 1;
    }

    // Extract alley numbers from filename
    const aAlley = extractAlleyFromFilename(a.fileName);
    const bAlley = extractAlleyFromFilename(b.fileName);

    // Use the alley identifiers for comparison if they exist, otherwise fall back to filename
    const aCompareValue = aAlley || a.fileName || '';
    const bCompareValue = bAlley || b.fileName || '';

    // Use localeCompare with numeric sorting for natural alphanumeric ordering
    const comparison = aCompareValue.localeCompare(bCompareValue, undefined, {
      numeric: true,
      sensitivity: 'base',
    });

    return opts.ascending ? comparison : -comparison;
  });
};
